"use client"

import { useState, useEffect, useCallback } from "react"
import "../styles/CreativeSpace.css"
import { auth, db } from "../config/firebase"
import { doc, onSnapshot } from 'firebase/firestore'
import axios from "axios"
import UserSelection from "../components/creativeSpace/UserSelection"
import CycleTracker from "../components/creativeSpace/CycleTracker"

const CreativeSpace = () => {
  // Cycle state
  const [currentCycle, setCurrentCycle] = useState({
    cycleStart: "",
    cycleEnd: "",
    phases: [],
    dailyEntries: [],
    averageCycleLength: 28,
    predictions: []
  })
  const [isDataInitialized, setIsDataInitialized] = useState(false)

  // UI state
  const [selectedDate, setSelectedDate] = useState("")
  const [showDailyForm, setShowDailyForm] = useState(false)
  const [currentPhase, setCurrentPhase] = useState("")
  const [phaseDates, setPhaseDates] = useState({})
  const [activeView, setActiveView] = useState("calendar")

  // User state
  const [userName, setUserName] = useState("")
  const [partnerName, setPartnerName] = useState("")
  const [showTracker, setShowTracker] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [partnerId, setPartnerId] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [viewingPartner, setViewingPartner] = useState(false)

  // Configuration options with colors
  const moodOptions = [
    { value: "Happy", color: "#FFD93D" },
    { value: "Calm", color: "#A8E6CF" },
    { value: "Energetic", color: "#FF6B6B" },
    { value: "Tired", color: "#C7CEEA" },
    { value: "Emotional", color: "#FFB5E8" },
    { value: "Anxious", color: "#B5B5B5" },
    { value: "Irritable", color: "#FFC078" },
    { value: "Sad", color: "#87CEEB" }
  ]

  const painLevels = [
    { value: "None", level: 0 },
    { value: "Mild", level: 1 },
    { value: "Moderate", level: 2 },
    { value: "Severe", level: 3 },
    { value: "Very Severe", level: 4 }
  ]

  const symptomOptions = [
    { value: "Cramps" },
    { value: "Bloating" },
    { value: "Headache" },
    { value: "Breast Tenderness" },
    { value: "Acne" },
    { value: "Fatigue" },
    { value: "Food Cravings" },
    { value: "Back Pain" },
    { value: "Nausea" },
    { value: "Hot Flashes" },
    { value: "Mood Swings" },
    { value: "Insomnia" }
  ]

  const phaseTypes = [
    { name: "Menstrual", color: "#E57373", days: "1-5" },
    { name: "Follicular", color: "#81C784", days: "6-13" },
    { name: "Ovulation", color: "#FFB74D", days: "14-16" },
    { name: "Luteal", color: "#9575CD", days: "17-28" }
  ]

  // Calculate current cycle day
  const getCurrentCycleDay = useCallback(() => {
    if (!currentCycle.cycleStart) return null
    const start = new Date(currentCycle.cycleStart)
    const today = new Date()
    const diffTime = Math.abs(today - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }, [currentCycle.cycleStart])

  // Get current phase based on cycle day
  const getCurrentPhaseInfo = useCallback(() => {
    const phase = currentCycle.phases.find(p => {
      const start = new Date(p.startDate)
      const end = new Date(p.endDate)
      const today = new Date()
      return today >= start && today <= end
    })
    return phase || null
  }, [currentCycle.phases])

  // Helper function to handle timezone issues
  const formatDateToUTC = (date) => {
    if (!date) return ""
    const d = new Date(date)
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split("T")[0]
  }

  // Helper function to parse UTC date string to local date
  const parseUTCDate = (dateString) => {
    if (!dateString) return null
    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day)
  }

  // Real-time listener for cycle data
  useEffect(() => {
    if (!selectedUserId || !isDataInitialized) return

    const cycleListener = onSnapshot(
      doc(db, "trackCycle", selectedUserId),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data()
          
          setCurrentCycle({
            cycleStart: data.cycleStart || "",
            cycleEnd: data.cycleEnd || "",
            phases: data.phases || [],
            dailyEntries: data.dailyEntries || [],
            averageCycleLength: data.averageCycleLength || 28,
            predictions: data.predictions || []
          })
          
          const initialPhaseDates = {}
          ;(data.phases || []).forEach(phase => {
            initialPhaseDates[phase.name] = {
              startDate: phase.startDate ? parseUTCDate(phase.startDate) : null,
              endDate: phase.endDate ? parseUTCDate(phase.endDate) : null
            }
          })
          setPhaseDates(initialPhaseDates)
        } else {
          setCurrentCycle({
            cycleStart: "",
            cycleEnd: "",
            phases: [],
            dailyEntries: [],
            averageCycleLength: 28,
            predictions: []
          })
          setPhaseDates({})
        }
      },
      (error) => {
        console.error("Error listening to cycle data:", error)
      }
    )

    return () => cycleListener()
  }, [selectedUserId, isDataInitialized])

  // Handle user selection
  const handleUserSelect = async (userType) => {
    try {
      setIsLoading(true)
      setIsDataInitialized(false)
      const userId = userType === 'user' ? auth.currentUser.uid : partnerId
      setViewingPartner(userType === 'partner')
      setSelectedUserId(userId)
      
      const response = await axios.get(`https://api.frazza.online/cycle/get?userId=${userId}`)
      
      if (response.data.success) {
        setCurrentCycle({
          ...response.data.data,
          averageCycleLength: response.data.data.averageCycleLength || 28,
          predictions: response.data.data.predictions || []
        })
        
        const initialPhaseDates = {}
        response.data.data.phases.forEach(phase => {
          initialPhaseDates[phase.name] = {
            startDate: phase.startDate ? parseUTCDate(phase.startDate) : null,
            endDate: phase.endDate ? parseUTCDate(phase.endDate) : null
          }
        })
        setPhaseDates(initialPhaseDates)
        setIsDataInitialized(true)
        setShowTracker(true)
      }
    } catch (error) {
      console.error("Error handling user selection:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Save cycle data
  useEffect(() => {
    const saveCycleData = async () => {
      try {
        if (!selectedUserId || !isDataInitialized) return

        await axios.post("https://api.frazza.online/cycle/save", {
          userId: selectedUserId,
          cycleData: currentCycle
        })
      } catch (error) {
        console.error("Error saving cycle data:", error)
      }
    }

    if (isDataInitialized) {
      saveCycleData()
    }
  }, [currentCycle, selectedUserId, isDataInitialized])

  // Fetch user info
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userId = auth.currentUser?.uid
        if (!userId) return

        const userResponse = await axios.get(`https://api.frazza.online/user/${userId}`)
        if (userResponse.data.success) {
          const name = userResponse.data.data.name
          if (name) {
            setUserName(name)
          }
        }

        const relationshipResponse = await axios.get(`https://api.frazza.online/connection/${userId}`)
        if (relationshipResponse.data.success && relationshipResponse.data.connected) {
          setPartnerName(relationshipResponse.data.partnerName || "Partner")
          setPartnerId(relationshipResponse.data.partnerId)
        }
      } catch (error) {
        console.error("Error fetching user information:", error)
      }
    }

    fetchUserInfo()
  }, [])

  // Handlers
  const handleCycleDateChange = (field, value) => {
    setCurrentCycle(prev => ({
      ...prev,
      [field]: value ? formatDateToUTC(value) : ""
    }))
  }

  const handlePhaseDateChange = (phaseName, dateType, date) => {
    setPhaseDates(prev => ({
      ...prev,
      [phaseName]: {
        ...prev[phaseName],
        [dateType]: date
      }
    }))

    const currentDates = phaseDates[phaseName] || {}
    const newStartDate = dateType === 'startDate' ? date : currentDates.startDate
    const newEndDate = dateType === 'endDate' ? date : currentDates.endDate

    const phaseInfo = phaseTypes.find((p) => p.name === phaseName)
    const newPhase = {
      name: phaseName,
      startDate: newStartDate ? formatDateToUTC(newStartDate) : "",
      endDate: newEndDate ? formatDateToUTC(newEndDate) : "",
      color: phaseInfo?.color || "#95a5a6",
    }

    setCurrentCycle((prev) => ({
      ...prev,
      phases: [...prev.phases.filter((p) => p.name !== phaseName), newPhase],
    }))
  }

  const handleDailyEntrySubmit = (entry) => {
    const newEntry = {
      ...entry,
      date: selectedDate,
      timestamp: new Date().toISOString()
    }

    setCurrentCycle((prev) => ({
      ...prev,
      dailyEntries: [...prev.dailyEntries.filter((e) => e.date !== selectedDate), newEntry],
    }))

    setShowDailyForm(false)
  }

  const handleDateSelect = (date, phase) => {
    setSelectedDate(date)
    setCurrentPhase(phase || "")
    setShowDailyForm(true)
  }

  const handleBack = () => {
    setShowTracker(false)
    setSelectedUserId(null)
    setViewingPartner(false)
    setIsDataInitialized(false)
    setActiveView("calendar")
    setCurrentCycle({
      cycleStart: "",
      cycleEnd: "",
      phases: [],
      dailyEntries: [],
      averageCycleLength: 28,
      predictions: []
    })
    setPhaseDates({})
  }

  // Render
  if (!showTracker) {
    return (
      <UserSelection
        userName={userName}
        partnerName={partnerName}
        partnerId={partnerId}
        onUserSelect={handleUserSelect}
        isLoading={isLoading}
      />
    )
  }

  return (
    <CycleTracker
      currentCycle={currentCycle}
      phaseDates={phaseDates}
      selectedDate={selectedDate}
      showDailyForm={showDailyForm}
      currentPhase={currentPhase}
      userName={viewingPartner ? partnerName : userName}
      partnerName={partnerName}
      onBack={handleBack}
      onCycleDateChange={handleCycleDateChange}
      onPhaseDateChange={handlePhaseDateChange}
      onDailyEntrySubmit={handleDailyEntrySubmit}
      onDailyFormCancel={() => setShowDailyForm(false)}
      onDateSelect={handleDateSelect}
      moodOptions={moodOptions}
      painLevels={painLevels}
      symptomOptions={symptomOptions}
      phaseTypes={phaseTypes}
      isLoading={isLoading}
      isPartnerView={viewingPartner}
      activeView={activeView}
      setActiveView={setActiveView}
      getCurrentCycleDay={getCurrentCycleDay}
      getCurrentPhaseInfo={getCurrentPhaseInfo}
    />
  )
}

export default CreativeSpace
